import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ToastProvider, useToast } from "../components/Toast";
import { useConfirm } from "../components/ConfirmDialog";
import AdminSidebar from "../components/AdminSidebar";
import {
  fetchTestimonialsAdmin,
  updateTestimonial,
  deleteTestimonial,
} from "../../services/api";

/* ─── Inline styles scoped to this page only ─────────────────────────────── */
const styles = `
  /* ── Page shell ── */
  .tpage-layout {
    display: grid;
    grid-template-columns: 220px minmax(0, 1fr);
    min-height: 100vh;
    padding-top: 64px;
    background: #f0f2f8;
  }
  @media (max-width: 768px) {
    .tpage-layout { grid-template-columns: 1fr; }
  }

  /* ── Content area ── */
  .tpage-content {
    padding: 36px 32px;
    min-width: 0;
    overflow-x: hidden;
    margin-top: 20px;
  }
  @media (max-width: 900px) {
    .tpage-content { padding: 24px 14px; }
  }

  /* ── Header row ── */
  .tpage-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 14px;
    margin-bottom: 26px;
  }

  .tpage-title {
    font-family: "Syne", sans-serif;
    font-size: clamp(18px, 3vw, 24px);
    font-weight: 800;
    color: #1a1a2e;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .tpage-title-count {
    font-size: 12px;
    font-weight: 400;
    color: #9090b0;
    background: #eef0f8;
    border: 1px solid #e2e5f0;
    border-radius: 100px;
    padding: 2px 10px;
  }

  /* ── Stats strip ── */
  .tpage-stats {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
    margin-bottom: 22px;
  }
  .tpage-stat-card {
    background: white;
    border: 1.5px solid #e2e5f0;
    border-radius: 14px;
    padding: 16px 18px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: 0 2px 8px rgba(98,70,234,0.04);
    transition: box-shadow 0.2s;
  }
  .tpage-stat-card:hover {
    box-shadow: 0 6px 20px rgba(98,70,234,0.1);
  }
  .tpage-stat-icon {
    width: 40px; height: 40px;
    border-radius: 11px;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
  }
  .tpage-stat-num {
    font-family: "Syne", sans-serif;
    font-size: 22px; font-weight: 800; color: #1a1a2e;
    line-height: 1;
  }
  .tpage-stat-label {
    font-size: 11px; color: #9090b0; margin-top: 2px;
  }

  /* ── Card wrapper ── */
  .tpage-card {
    background: white;
    border: 1.5px solid #e2e5f0;
    border-radius: 18px;
    box-shadow: 0 2px 12px rgba(98,70,234,0.05);
    overflow: hidden;
  }

  /* ── Search / filter bar inside card ── */
  .tpage-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    padding: 14px 18px;
    border-bottom: 1px solid #e2e5f0;
    background: #f7f8fc;
  }
  .tpage-search {
    flex: 1;
    min-width: 180px;
    background: white;
    border: 1.5px solid #e2e5f0;
    border-radius: 9px;
    padding: 8px 12px 8px 34px;
    font-size: 13px;
    color: #1a1a2e;
    font-family: "DM Sans", sans-serif;
    outline: none;
    transition: border-color 0.2s;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%239090b0' stroke-width='2.5'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cpath d='m21 21-4.35-4.35'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: 10px center;
  }
  .tpage-search:focus { border-color: #6246ea; }

  .tpage-filter-chip {
    background: white;
    border: 1.5px solid #e2e5f0;
    border-radius: 100px;
    padding: 6px 13px;
    font-size: 12px;
    color: #5a5a80;
    cursor: pointer;
    transition: all 0.18s;
    font-family: "DM Sans", sans-serif;
    white-space: nowrap;
  }
  .tpage-filter-chip:hover,
  .tpage-filter-chip.active {
    background: rgba(98,70,234,0.08);
    border-color: #6246ea;
    color: #6246ea;
  }

  /* ── Scrollable table wrapper — THE KEY ── */
  .tpage-table-wrap {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: #6246ea #eef0f8;
  }
  .tpage-table-wrap::-webkit-scrollbar { height: 4px; }
  .tpage-table-wrap::-webkit-scrollbar-track { background: #eef0f8; }
  .tpage-table-wrap::-webkit-scrollbar-thumb { background: #6246ea; border-radius: 4px; }

  /* ── Table ── */
  .tpage-table {
    width: 100%;
    border-collapse: collapse;
    white-space: nowrap;   /* prevents wrapping → enables h-scroll */
    min-width: 750px;      /* trigger scroll below this width */
  }
  .tpage-table th {
    text-align: left;
    padding: 11px 16px;
    font-size: 10px;
    font-weight: 700;
    color: #9090b0;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    border-bottom: 1.5px solid #e2e5f0;
    background: #f7f8fc;
    position: sticky; top: 0; z-index: 1;
    white-space: nowrap;
  }
  .tpage-table td {
    padding: 13px 16px;
    font-size: 13px;
    color: #1a1a2e;
    border-bottom: 1px solid #f0f2f8;
    vertical-align: middle;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .tpage-table tr:last-child td { border-bottom: none; }
  .tpage-table tbody tr {
    transition: background 0.15s;
    cursor: default;
  }
  .tpage-table tbody tr:hover td { background: #f7f8fc; }
  .tpage-table tbody tr.inactive td { opacity: 0.45; }

  /* ── Person cell ── */
  .tpage-person {
    display: flex; align-items: center; gap: 10px;
  }
  .tpage-avatar {
    width: 34px; height: 34px;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700;
    font-family: "Syne", sans-serif;
    flex-shrink: 0;
    border: 2px solid rgba(255,255,255,0.7);
    box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  }
  .tpage-person-name {
    font-weight: 700; font-size: 13px; color: #1a1a2e;
    white-space: nowrap;
  }
  .tpage-person-role {
    font-size: 11px; color: #9090b0;
  }

  /* ── Stars ── */
  .tpage-stars { color: #f59e0b; font-size: 12px; letter-spacing: 1px; }

  /* ── Status badge ── */
  .tpage-badge {
    display: inline-flex; align-items: center; gap: 5px;
    border-radius: 100px; padding: 3px 10px;
    font-size: 11px; font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none; background: none;
    font-family: "DM Sans", sans-serif;
  }
  .tpage-badge::before {
    content: ""; width: 6px; height: 6px;
    border-radius: 50%; display: block; flex-shrink: 0;
  }
  .tpage-badge.active-badge {
    background: rgba(16,185,129,0.1); color: #10b981;
  }
  .tpage-badge.active-badge::before { background: #10b981; }
  .tpage-badge.inactive-badge {
    background: rgba(255,107,53,0.1); color: #ff6b35;
  }
  .tpage-badge.inactive-badge::before { background: #ff6b35; }
  .tpage-badge:hover { filter: brightness(0.9); transform: scale(0.97); }

  /* ── Action buttons ── */
  .tpage-actions { display: flex; gap: 6px; align-items: center; }
  .tpage-action-btn {
    width: 30px; height: 30px;
    border-radius: 8px;
    border: 1.5px solid #e2e5f0;
    background: white;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    font-size: 13px;
    transition: all 0.18s;
  }
  .tpage-action-btn:hover { transform: scale(1.1); }
  .tpage-action-btn.edit:hover { border-color: #6246ea; background: rgba(98,70,234,0.06); }
  .tpage-action-btn.del:hover  { border-color: #ef4444; background: rgba(239,68,68,0.06); }

  /* ── Empty state ── */
  .tpage-empty {
    text-align: center; padding: 60px 20px;
    color: #9090b0;
  }
  .tpage-empty-icon { font-size: 48px; margin-bottom: 14px; }
  .tpage-empty-title { font-family: "Syne", sans-serif; font-weight: 700; font-size: 16px; color: #1a1a2e; margin-bottom: 6px; }
  .tpage-empty-sub { font-size: 13px; color: #9090b0; }

  /* ── Loading shimmer ── */
  .tpage-shimmer-row td {
    padding: 16px;
  }
  .tpage-shimmer {
    height: 14px; border-radius: 7px;
    background: linear-gradient(90deg, #eef0f8 25%, #e2e5f0 50%, #eef0f8 75%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
  }
  @keyframes shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  /* ── Pagination / footer row ── */
  .tpage-footer {
    display: flex; align-items: center; justify-content: space-between;
    flex-wrap: wrap; gap: 10px;
    padding: 12px 18px;
    border-top: 1px solid #e2e5f0;
    background: #f7f8fc;
    font-size: 12px; color: #9090b0;
  }

  /* ── Add button ── */
  .tpage-add-btn {
    display: flex; align-items: center; gap: 6px;
    background: #6246ea; color: white;
    border: none; border-radius: 10px;
    padding: 9px 18px;
    font-size: 13px; font-weight: 600;
    cursor: pointer;
    font-family: "DM Sans", sans-serif;
    box-shadow: 0 4px 14px rgba(98,70,234,0.3);
    transition: all 0.2s;
    white-space: nowrap;
  }
  .tpage-add-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(98,70,234,0.4);
  }
`;

/* ── Status Badge component ── */
const StatusBadge = ({ active, onClick }) => (
  <button
    className={`tpage-badge ${active ? "active-badge" : "inactive-badge"}`}
    onClick={onClick}
    title="Click to toggle"
  >
    {active ? "Active" : "Inactive"}
  </button>
);

/* ── Shimmer rows while loading ── */
const ShimmerRows = () =>
  Array.from({ length: 4 }).map((_, i) => (
    <tr key={i} className="tpage-shimmer-row">
      {Array.from({ length: 7 }).map((_, j) => (
        <td key={j}>
          <div
            className="tpage-shimmer"
            style={{ width: j === 0 ? "60%" : j === 4 ? "80%" : "55%" }}
          />
        </td>
      ))}
    </tr>
  ));

/* ─── Main inner component ────────────────────────────────────────────────── */
const TestimonialsInner = () => {
  const toast = useToast();
  const { confirm, ConfirmComponent } = useConfirm();
  const navigate = useNavigate();

  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | active | inactive

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchTestimonialsAdmin();
      setTestimonials(r.data.data || []);
    } catch {
      toast("Failed to load testimonials", "error");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Delete testimonial?",
      message: "This action cannot be undone.",
      confirmText: "Delete",
    });
    if (!ok) return;
    try {
      await deleteTestimonial(id);
      toast("Deleted!", "success");
      load();
    } catch {
      toast("Failed to delete", "error");
    }
  };

  const handleToggleActive = async (t) => {
    try {
      await updateTestimonial(t.id, { isActive: !t.isActive });
      load();
    } catch {
      toast("Failed to update status", "error");
    }
  };

  /* Filtered list */
  const filtered = testimonials.filter((t) => {
    const matchFilter =
      filter === "all" ||
      (filter === "active" && t.isActive) ||
      (filter === "inactive" && !t.isActive);
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      t.name?.toLowerCase().includes(q) ||
      t.company?.toLowerCase().includes(q) ||
      t.course?.toLowerCase().includes(q);
    return matchFilter && matchSearch;
  });

  const activeCount = testimonials.filter((t) => t.isActive).length;
  const inactiveCount = testimonials.length - activeCount;
  const avgRating = testimonials.length
    ? (
        testimonials.reduce((s, t) => s + (t.rating || 0), 0) /
        testimonials.length
      ).toFixed(1)
    : "—";

  return (
    <>
      {/* Inject scoped CSS */}
      <style>{styles}</style>
      {ConfirmComponent}

      <div className="tpage-layout">
        <AdminSidebar
          section="testimonials"
          onSectionChange={(id) =>
            navigate("/admin", { state: { section: id } })
          }
        />

        <div className="tpage-content">
          {/* ── Header ── */}
          <div className="tpage-header">
            <div className="tpage-title">
              💬 Testimonials
              <span className="tpage-title-count">
                {testimonials.length} total
              </span>
            </div>
            <button
              className="tpage-add-btn"
              onClick={() => navigate("/admin/add-testimonial")}
            >
              + Add Testimonial
            </button>
          </div>

          {/* ── Stats strip ── */}
          <div className="tpage-stats">
            <div className="tpage-stat-card">
              <div
                className="tpage-stat-icon"
                style={{ background: "rgba(98,70,234,0.1)" }}
              >
                💬
              </div>
              <div>
                <div className="tpage-stat-num">{testimonials.length}</div>
                <div className="tpage-stat-label">Total</div>
              </div>
            </div>
            <div className="tpage-stat-card">
              <div
                className="tpage-stat-icon"
                style={{ background: "rgba(16,185,129,0.1)" }}
              >
                ✅
              </div>
              <div>
                <div className="tpage-stat-num">{activeCount}</div>
                <div className="tpage-stat-label">Active</div>
              </div>
            </div>
            <div className="tpage-stat-card">
              <div
                className="tpage-stat-icon"
                style={{ background: "rgba(255,107,53,0.1)" }}
              >
                ⏸️
              </div>
              <div>
                <div className="tpage-stat-num">{inactiveCount}</div>
                <div className="tpage-stat-label">Inactive</div>
              </div>
            </div>
            <div className="tpage-stat-card">
              <div
                className="tpage-stat-icon"
                style={{ background: "rgba(245,158,11,0.1)" }}
              >
                ⭐
              </div>
              <div>
                <div className="tpage-stat-num">{avgRating}</div>
                <div className="tpage-stat-label">Avg Rating</div>
              </div>
            </div>
          </div>

          {/* ── Main card ── */}
          <div className="tpage-card">
            {/* Toolbar */}
            <div className="tpage-toolbar">
              <input
                className="tpage-search"
                placeholder="Search by name, company, course…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {["all", "active", "inactive"].map((f) => (
                <button
                  key={f}
                  className={`tpage-filter-chip ${filter === f ? "active" : ""}`}
                  onClick={() => setFilter(f)}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>

            {/* Scrollable table */}
            <div className="tpage-table-wrap">
              <table className="tpage-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Person</th>
                    <th>Company</th>
                    <th>Course</th>
                    <th>Rating</th>
                    <th>Testimonial</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <ShimmerRows />
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={8}>
                        <div className="tpage-empty">
                          <div className="tpage-empty-icon">💬</div>
                          <div className="tpage-empty-title">
                            {search || filter !== "all"
                              ? "No results found"
                              : "No testimonials yet"}
                          </div>
                          <div className="tpage-empty-sub">
                            {search || filter !== "all"
                              ? "Try adjusting your search or filter."
                              : "Add your first testimonial using the button above."}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((t, idx) => (
                      <tr key={t.id} className={t.isActive ? "" : "inactive"}>
                        <td style={{ color: "#9090b0", fontSize: 12 }}>
                          {idx + 1}
                        </td>

                        {/* Person */}
                        <td>
                          <div className="tpage-person">
                            <div
                              className="tpage-avatar"
                              style={{
                                background: t.avatarBg || "#6246ea",
                                color: t.avatarCo || "#fff",
                              }}
                            >
                              {t.avatar || t.name?.[0]}
                            </div>
                            <div>
                              <div className="tpage-person-name">{t.name}</div>
                              <div className="tpage-person-role">{t.role}</div>
                            </div>
                          </div>
                        </td>

                        {/* Company */}
                        <td style={{ fontWeight: 500 }}>{t.company}</td>

                        {/* Course */}
                        <td
                          style={{
                            maxWidth: 160,
                            color: "#5a5a80",
                            fontSize: 12,
                          }}
                        >
                          {t.course}
                        </td>

                        {/* Rating */}
                        <td>
                          <span className="tpage-stars">
                            {"★".repeat(t.rating)}
                            <span style={{ color: "#e2e5f0" }}>
                              {"★".repeat(5 - t.rating)}
                            </span>
                          </span>
                        </td>

                        {/* Text */}
                        <td
                          style={{
                            maxWidth: 220,
                            color: "#5a5a80",
                            fontSize: 12,
                          }}
                        >
                          {t.text}
                        </td>

                        {/* Status */}
                        <td>
                          <StatusBadge
                            active={t.isActive}
                            onClick={() => handleToggleActive(t)}
                          />
                        </td>

                        {/* Actions */}
                        <td>
                          <div className="tpage-actions">
                            <button
                              className="tpage-action-btn edit"
                              onClick={() =>
                                navigate(`/admin/edit-testimonial/${t.id}`)
                              }
                              title="Edit"
                            >
                              ✏️
                            </button>
                            <button
                              className="tpage-action-btn del"
                              onClick={() => handleDelete(t.id)}
                              title="Delete"
                            >
                              🗑
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer row */}
            {!loading && filtered.length > 0 && (
              <div className="tpage-footer">
                <span>
                  Showing <strong>{filtered.length}</strong> of{" "}
                  <strong>{testimonials.length}</strong> testimonials
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ color: "#10b981", fontWeight: 600 }}>
                    {activeCount} active
                  </span>
                  &nbsp;·&nbsp;
                  <span style={{ color: "#ff6b35", fontWeight: 600 }}>
                    {inactiveCount} inactive
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

/* ─── Page wrapper with Toast ─────────────────────────────────────────────── */
const TestimonialsPage = () => (
  <ToastProvider>
    <TestimonialsInner />
  </ToastProvider>
);

export default TestimonialsPage;
